import { useQuery, useQueries } from '@tanstack/react-query';
import type { DocumentItem } from '@odo/shared-ui';
import { fetchDxReportVersions } from '../services/patient/dxReport';
import { fetchSdiVersions, type SdiVersion } from '../services/patient/sdi';
import { fetchRdiVersions } from '../services/patient/rdi';
import { getDownloadUrl } from '../services/cloud-files';

/**
 * Builds the Overview document list for one document type from its timeline
 * versions. Every document has one — in-app submissions get theirs on creation,
 * and documents imported off Monday get a version-0 event carrying the original
 * filename and filing date. The timeline is the only read path; nothing is read
 * from Monday.
 */
function useGcsDocItems(
    patientId: string | null,
    versionsQueryKey: unknown[],
    fetchVersions: (id: string) => Promise<SdiVersion[]>
): DocumentItem[] {
    const { data: versions = [] } = useQuery({
        queryKey: versionsQueryKey,
        queryFn: () => fetchVersions(patientId!),
        enabled: !!patientId,
        staleTime: 0
    });

    const urlResults = useQueries({
        queries: versions.map((v) => ({
            queryKey: ['download-url', patientId, v.patientFileId],
            queryFn: () => getDownloadUrl(patientId!, v.patientFileId),
            enabled: !!patientId,
            // Signed URLs expire after 1 hour — refresh at 30 min
            staleTime: 30 * 60 * 1000
        }))
    });

    return versions
        .map((v, i): DocumentItem | null => {
            const url = urlResults[i]?.data;
            if (!url) return null;
            return { id: v.patientFileId, name: v.fileName, url, date: v.createdAt };
        })
        .filter((d): d is DocumentItem => d !== null);
}

export function useOverviewDocAssets(patientId: string | null): {
    dxReportDocs: DocumentItem[];
    sdiDocs: DocumentItem[];
    rdiDocs: DocumentItem[];
} {
    const dxReportDocs = useGcsDocItems(
        patientId,
        ['patient', patientId, 'dx-versions'],
        fetchDxReportVersions
    );

    const sdiDocs = useGcsDocItems(
        patientId,
        ['patient', patientId, 'sdi-versions'],
        fetchSdiVersions
    );

    const rdiDocs = useGcsDocItems(
        patientId,
        ['patient', patientId, 'rdi-versions'],
        fetchRdiVersions
    );

    return { dxReportDocs, sdiDocs, rdiDocs };
}
